<template>
    <div>
      <main-layout
        controlsBg="light"
        :hasControlsBar="false"
        :breadcrumb="getBreadcrumb">
        <div
          class="container">
          <grid-row
            column-align="center">
            <grid-col
              col-size="4">
              <h1
                class="header margin-top-xl">
                <div>
                  <chevron
                    @click="goBack()"/>
                    {{ title }}
                </div>
              </h1>
              <alert
                v-if="alertVisible"
                :message="alertMessage"
                :type="alertType">
              </alert>
            </grid-col>
          </grid-row>
          <grid-row
            column-align="center">
            <grid-col
              col-size="4">
              <label-variable-multiple-selector
                :label="$t('rule.variable')"
                :variables="getVariablesMinimal"
                :taggedVariables="getTaggedVariables"
                :tree="getTagsTree"
                :error="selectedVariablesError"
                v-model="selectedVariables"
                v-on:tagsChanged="tagsChanged"
                :errorMessage="$t('commons.mandatoryFieldMessage')"
                class="variable-selector" />
            </grid-col>
          </grid-row>
          <grid-row
            column-align="center">
            <grid-col
              col-size="4">
              <div class="assign-category">
                {{ $t('variableManagement.assignCategories') }}
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <transition-group name="fade">
                <div
                  v-for="(option, index) in categoryOptions"
                  :key="index"
                  class="selection-wrapper">
                  <category-option
                    v-model="categoryOptions[index]"
                    :value-error="categoryOptionsErrors[index] !== undefined ? categoryOptionsErrors[index].value : false"
                    :name-error="categoryOptionsErrors[index] !== undefined ? categoryOptionsErrors[index].name : false"/>
                  <font-awesome-icon
                    class="remove-option"
                    :class="isRemoveButtonDisabled() ? 'selection-right-disabled' : 'selection-right opacity'"
                    v-on:click="onClickRemoveOption(index)"
                    :icon="icon.remove"/>
                </div>
              </transition-group>
              <div
                class="add-wrapper">
                <btn-pill
                  :disabled="isAddButtonDisabled()"
                  v-on:click="onClickAddCategory()"
                  btn-size="s"
                  btn-type="secondary">
                  <font-awesome-icon :icon="icon.add"/>
                  {{ $t('variableManagement.addCategory') }}
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <label-input
                class="group-name-label"
                form-size="m"
                :label="$t('variableManagement.groupName')"
                :type="'text'"
                v-model="groupName"
                :error="groupNameError"/>
            </grid-col>
          </grid-row>
          <grid-row
            column-align="center">
            <grid-col
              col-size="4">
              <div
                class="align-right margin-bottom-l">
                <btn-pill
                  :btnText="$t('commons.cancel')"
                  v-on:click="goBack"
                  btn-size="m"
                  btn-type="secondary">
                </btn-pill>
                <btn-pill
                  :btnText="$t('commons.save')"
                  v-on:click="onSave"
                  btn-size="m">
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
        </div>
      </main-layout>
    </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import Chevron from '@/components/shared/Chevron';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelVariableMultipleSelector from '@/components/variable/LabelVariableMultipleSelector';
import {mapActions, mapGetters} from 'vuex';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import {
  faPlusCircle,
  faTimesCircle
} from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import CategoryOption from '@/components/variable/CategoryOption';
import Alert from '@/components/shared/alert/Alert';
import variableApi from '@/api/variable';
import variableSelectorUtils from '@/layouts/variableSelectorUtils';

export default {
  name: 'VariableCategory',
  components: {
    CategoryOption,
    MainLayout,
    Chevron,
    GridRow,
    GridCol,
    LabelVariableMultipleSelector,
    LabelInput,
    BtnPill,
    FontAwesomeIcon,
    Alert
  },
  mixins: [
    variableSelectorUtils
  ],
  data () {
    return {
      icon: {
        add: faPlusCircle,
        remove: faTimesCircle
      },
      editCategory: {},
      selectedVariables: [],
      categoryOptions: [
        {
          value: undefined,
          name: undefined,
          color: {
            type: 'rgb',
            channels: [0, 0, 0]
          }
        }
      ],
      categoryOptionsErrors: [
        {
          value: false,
          name: false
        }
      ],
      groupName: '',
      /* Alert */
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      /* Form field errors */
      selectedVariablesError: false,
      groupNameError: false,
      variablesMinimal: [],
      taggedVariables: []
    };
  },
  created () {
    variableApi.getMinimalVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
    });
    this.retrieveTagsTree({context: this});

    this.editCategory = this.$route.params;
    if (this.isEdition()) {
      this.selectedVariables = this.editCategory.variables;
      this.categoryOptions = this.editCategory.ranges;
      this.categoryOptions.forEach(option => {
        this.categoryOptionsErrors.push({
          value: false,
          name: false
        });
      });
      this.groupName = this.editCategory.name;
    }
  },
  computed: {
    ...mapGetters([
      'getTagsTree'
    ]),
    getBreadcrumb () {
      return [
        { name: this.$t('menu.workspace'), link: '/workspace' },
        { name: this.$t('variableManagement.title'), link: '/variable-management' },
        { name: this.$t('variableManagement.categoricalVariables'), link: 'categorical-variable/list' },
        { name: this.title, link: '/variable-management', disabled: true }
      ];
    },
    title () {
      return this.isEdition()
        ? this.$t('variableManagement.updateCategoricalVariable')
        : this.$t('variableManagement.createCategoricalVariable');
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      return this.taggedVariables;
    }
  },
  methods: {
    ...mapActions([
      'retrieveTagsTree',
      'createCategory',
      'updateCategory'
    ]),
    goBack () {
      this.$router.push({name: 'VariableCategoryList'});
    },
    isEdition () {
      return this.editCategory.id !== undefined;
    },
    onSave () {
      clearErrorPrompts(this);
      if (isValidForm(this)) {
        if (this.isEdition()) {
          const category = {
            id: this.editCategory.id,
            name: this.groupName,
            ranges: this.categoryOptions,
            variables: this.selectedVariables
          };
          updateCategory(this, category);
        } else {
          const category = {
            name: this.groupName,
            ranges: this.categoryOptions,
            variables: this.selectedVariables
          };
          createCategory(this, category);
        }
      }
    },
    isAddButtonDisabled () {
      let disabled = false;

      // All with data
      let currentNumberOfOptions = this.categoryOptions.length;
      for (let i = 0; i < currentNumberOfOptions; i++) {
        const option = this.categoryOptions[i];
        if (option === undefined || option === null) {
          disabled = true;
          break;
        }
      }

      return disabled;
    },
    onClickAddCategory () {
      this.categoryOptions.push({
        value: undefined,
        name: undefined,
        color: {
          type: 'rgb',
          channels: [0, 0, 0]
        }
      });
      this.categoryOptionsErrors.push({
        value: false,
        name: false
      });
    },
    isRemoveButtonDisabled () {
      return this.categoryOptions.length === 1;
    },
    onClickRemoveOption (index) {
      if (!this.isRemoveButtonDisabled()) {
        this.categoryOptions.splice(index, 1);
      }
    }
  }
};

const createCategory = (context, category) => {
  context.createCategory({context: context, category}).then(() => {
    const msg = context.$t('variableManagement.successCreate', {name: category.groupName});
    context.$router.replace({ name: 'VariableCategoryList', params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('variableManagement.errorCreate', {name: category.groupName}), 'error');
  });
};

const updateCategory = (context, category) => {
  context.updateCategory({context: context, category}).then(() => {
    const msg = context.$t('variableManagement.successUpdate', {name: category.groupName});
    context.$router.replace({ name: 'VariableCategoryList', params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('variableManagement.errorUpdate', {name: category.groupName}), 'error');
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const isValidForm = context => {
  let hasError = false;
  if (!context.groupName || context.groupName.length < 1) {
    context.groupNameError = true;
    hasError = true;
  }

  if (context.selectedVariables.length === 0) {
    context.selectedVariablesError = true;
    hasError = true;
  }

  context.categoryOptions.forEach((option, index) => {
    if (isNaN(option.value)) {
      context.categoryOptionsErrors[index].value = true;
      hasError = true;
    }
    if (option.name === undefined || option.name.length === 0) {
      context.categoryOptionsErrors[index].name = true;
      hasError = true;
    }
  });

  return !hasError;
};

const clearErrorPrompts = context => {
  context.groupNameError = false;
  context.selectedVariablesError = false;
  context.categoryOptionsErrors.forEach(option => {
    option.value = false;
    option.name = false;
  });
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .variable-selector {
    margin-top: $sp-margin-large;
    width: 50%;
  }

  .assign-category {
    margin-top: $sp-margin-larger;
    font-size: 1.2rem;
    margin-bottom: $sp-margin-small;
  }

  .variable-input-field {
    display: inline-block;
  }

  .group-name-label {
    width: 75%;
  }

  .add-wrapper {
    margin-bottom: $sp-margin-larger;
  }

  .selection-wrapper {
    display: flex;
    align-items: center;
  }

  .selection-left {
    width: 100%;
    margin: 1px;
    margin-right: $sp-margin-small;
  }

  .selection-right {
    font-size: $fs-xl;
    cursor: pointer;
    color: $primary;
  }

  .selection-right-disabled {
    display: none;
  }

  .align-right {
    float: right;
    clear:both;
  }

  .remove-option {
    margin-bottom: $sp-margin-small;
    margin-left: $sp-margin;
  }

  /deep/ .form-group {
    margin-bottom: $sp-margin-smaller !important;
  }

  .opacity:hover {
    opacity: 0.3;
    transition: opacity 0.3s;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/variables/VariableCategory.vue